<template>
  <div
    :class="`card nx-hole-column-card ${classes} ${
      isStartHole ? 'nx-hole-column-start' : ''
    } ${hole?.offset?.value != null ? (isLate ? 'late' : 'early') : ''} ${
      hole?.isFirstAffectedByDelay ? 'delayed' : ''
    } ${$root.enableHoleSettingsModal() ? 'clickable' : ''}`"
  >
    <div class="card-body text-center p-0">
      <div
        class="d-flex justify-content-between align-items-end"
        style="padding-left: 3px; padding-right: 3px"
      >
        <div>
          <span class="text-muted">{{ hole.number }}</span>
        </div>
        <div>
          <strong>{{
            hole?.offset?.value > 0
              ? `+${hole?.offset?.value}`
              : hole?.offset?.value
          }}</strong>
        </div>
      </div>
      <div
        class="d-flex justify-content-between align-items-center"
        style="min-height: 18px; padding-left: 3px; padding-right: 3px"
        v-if="compact === false"
      >
        <div class="display-inline-block">
          <span
            v-if="
              hole?.refereeDecisions?.length > 0 &&
              $root.can('refereeDecisions:read')
            "
            style="line-height: 1; font-size: 0.7rem"
            >R</span
          ><i
            class="bi bi-chevron-up fw-bold"
            v-if="hole?.isOvertakingHole && $root.can('flightOvertakes:read')"
          ></i>
        </div>
        <div>
          <span class="badge rounded-pill bg-danger">{{
            displayedInitials
          }}</span>
        </div>
      </div>
      <div class="progress">
        <div
          :class="`progress-bar ${
            hole?.offset?.value != null
              ? isLate
                ? 'bg-danger'
                : 'bg-success'
              : progress.class
          }`"
          :style="{
            width: `${
              hole?.offset?.value != null ? 100 : progress.percentage
            }%`,
          }"
        ></div>
        <span
          :class="`fw-normal ${
            hole?.offset?.value != null
              ? hole?.offset?.value <= 0
                ? 'text-dark'
                : 'text-white'
              : progress.color
          }`"
          >{{ hole.targetTimeFormatted }}</span
        >
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapGetters } from "vuex";

export default defineComponent({
  name: "HoleColumn",
  components: {},
  props: {
    classes: String,
    hole: Object,
    isStartHole: Boolean,
    compact: Boolean,
  },
  computed: {
    ...mapGetters({
      timestamp: "timeSheets/getTimestamp",
    }),
    isLate() {
      return this.hole?.offset?.value > 0;
    },
    displayedInitials() {
      let initials = "";
      if (
        this.hole?.flightOvertakeUserInitials?.length > 0 &&
        this.$root.can("flightOvertakes:read")
      ) {
        initials = this.hole?.flightOvertakeUserInitials;
      }
      if (
        this.hole?.refereeDecisions?.length > 0 &&
        this.$root.can("refereeDecisions:read")
      ) {
        initials = this.hole?.refereeDecisions[0].userInitials;
      }
      if (this.hole?.offset != null) {
        initials = this.hole?.offset?.userInitials;
      }
      return initials;
    },
  },
  methods: {
    calculateProgress(nextTimestamp) {
      var percentage = 100;
      var classNames = " bg-secondary";
      var color = "text-white";

      const currentHoleTargetTime = this.hole.targetTimeMillis;
      const currentHoleToNowDeltaTime = currentHoleTargetTime - nextTimestamp;

      if (
        nextTimestamp >= this.hole.prevHoleTargetTimeMillis &&
        nextTimestamp <= currentHoleTargetTime
      ) {
        const remainingSeconds = (currentHoleTargetTime - nextTimestamp) / 1000;
        if (remainingSeconds <= 120) {
          classNames = " bg-danger";
        } else if (remainingSeconds <= 300) {
          classNames = " bg-warning";
        } else {
          classNames = " bg-success";
        }

        const absoluteSeconds =
          (currentHoleTargetTime - this.hole.prevHoleTargetTimeMillis) / 1000;

        percentage = Math.ceil(
          ((absoluteSeconds - remainingSeconds) / absoluteSeconds) * 100
        );
        color = "text-black";
      } else if (currentHoleToNowDeltaTime > 0) {
        percentage = 0;
      }

      return {
        percentage: percentage,
        class: classNames,
        color,
      };
    },
    setProgress() {
      const next = this.calculateProgress(this.timestamp);
      if (
        this.progress.class != next.class ||
        this.progress.percentage != next.percentage
      ) {
        this.progress = next;
      }
    },
  },
  data() {
    return {
      value: null,
      progress: {
        class: " bg-secondary",
        percentage: 0,
      },
    };
  },
  created() {
    this.setProgress();
  },
  watch: {
    timestamp() {
      if (this.hole.offset != null) {
        this.setProgress();
      }
    },
    hole() {
      this.setProgress();
    },
  },
});
</script>

<style lang="scss">
@import "~/node_modules/bootstrap/scss/_functions.scss";
@import "~/node_modules/bootstrap/scss/_variables.scss";
@import "~/node_modules/bootstrap/scss/_mixins.scss";

.card.nx-hole-column-card {
  border-radius: 8px;
  background-color: $gray-100;

  min-width: 36px;
  font-size: 0.7rem;

  &.clickable:hover {
    background-color: $gray-200;
    cursor: pointer;
  }

  &.early {
    background-color: $green-100;

    &.clickable:hover {
      background-color: $green-200;
    }
  }

  &.late {
    background-color: $red-200;

    &.clickable:hover {
      background-color: $red-300;
    }
  }

  .badge {
    padding: 0.125em 0.325em;
  }

  .progress {
    border-radius: 0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;

    span {
      font-size: 0.7rem;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }

    .bg-success {
      background-color: #6fd86f !important;
    }
  }
}

.card-text.nx-hole-column-start {
  background-color: #efefef;
}

.badge.nx-hole-column-badge {
  font-size: 0.6rem;
}

.progress .progress-bar {
  -webkit-transition: none;
  -moz-transition: none;
  -ms-transition: none;
  -o-transition: none;
  transition: none;
}

.delayed {
  box-shadow: 0px 2px 0px 0px rgba(220, 53, 69, 0.3) inset;
}
</style>
